import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertTitle, Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { runManualPostIngestPipeline } from 'api/platform';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { BrightfieldPostIngestStep, MifPostIngestStep, Study } from 'interfaces/study';
import { filter, find, last, map, size, sumBy } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import * as yup from 'yup';

import { ActionStepper } from '../ActionStepper';
import { ChannelsController } from '../ChannelsController';
import { JobNameAndDescriptionStep } from '../utils';
import {
  PostIngestFamilySelection,
  PostIngestStainFamily,
  usePostIngestPipelineSelection,
} from './usePostIngestPipelineSelection';

const SNACK_BAR_KEY_RUN_MANUAL_POST_INGEST_PIPELINE = 'RUN_MANUAL_POST_INGEST_PIPELINE';
const REQUIRED_BRIGHTFIELD_PIPELINE_ACTIONS = ['Cloud optimization', 'Tissue segmentation'];
const REQUIRED_MIF_PIPELINE_ACTIONS = ['Cloud optimization', 'Tissue segmentation', 'Histogram creation'];
const DNA_TILE_PERCENTILE_CAPS_ACTION = 'DNA tile percentile caps';

const brightfieldStepLabels: Record<BrightfieldPostIngestStep, string> = {
  cellSegmentation: 'Cell segmentation',
};

const mifStepLabels: Record<MifPostIngestStep, string> = {
  normalization: 'Normalization',
  cellSegmentation: 'Cell segmentation',
};

const stainFamilyLabels: Record<PostIngestStainFamily, string> = {
  brightfield: 'Brightfield',
  mIF: 'mIF',
};

export interface ManualPostIngestFormValues {
  dnaChannels?: string[];
  jobName?: string;
  jobDescription?: string;
}

const validationSchema = [
  yup.object({}),
  yup.object({
    jobName: yup.string().optional().nullable(),
    jobDescription: yup.string().optional().nullable(),
  }),
];

const defaultValues: ManualPostIngestFormValues = { dnaChannels: [], jobName: '', jobDescription: '' };

interface Props {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  study?: Study;
}

const getConfiguredPipelineActions = (stainFamily: PostIngestStainFamily, study?: Study): string[] => {
  if (stainFamily === 'brightfield') {
    const additionalSteps = study?.settings?.postIngestPipeline?.brightfield?.additionalSteps ?? [];
    return [...REQUIRED_BRIGHTFIELD_PIPELINE_ACTIONS, ...map(additionalSteps, (step) => brightfieldStepLabels[step])];
  }

  const mifSettings = study?.settings?.postIngestPipeline?.mIF;
  const additionalSteps = mifSettings?.additionalSteps ?? [];
  return [
    ...REQUIRED_MIF_PIPELINE_ACTIONS,
    ...map(additionalSteps, (step) => mifStepLabels[step]),
    ...(mifSettings?.dnaTilePercentileCapsEnabled ? [DNA_TILE_PERCENTILE_CAPS_ACTION] : []),
  ];
};

const buildFamilyRequests = ({
  familySelections,
  studyId,
  values,
  dnaChannel,
}: {
  familySelections: PostIngestFamilySelection[];
  studyId: string;
  values: ManualPostIngestFormValues;
  dnaChannel?: string;
}) =>
  map(familySelections, ({ stainFamily, casesParams }) => {
    const enteredJobName = values.jobName?.trim();
    const familyJobName =
      enteredJobName && size(familySelections) > 1
        ? `${enteredJobName} - ${stainFamilyLabels[stainFamily]}`
        : enteredJobName;

    return {
      studyId,
      stainFamily,
      slideIds: casesParams.slideIdsToInclude ?? [],
      ...(stainFamily === 'mIF' ? { dnaChannel } : {}),
      jobName: familyJobName || undefined,
      jobDescription: values.jobDescription || undefined,
    };
  });

export const RunManualPostIngestPipeline = ({ onClose, casesParams, study }: Props): ReactElement => {
  const studyId = casesParams.studyId ?? casesParams.filters?.studyId;
  const { familySelections, isLoadingSelection, selectionError, hasSelectedSlides } =
    usePostIngestPipelineSelection(casesParams);
  const pipelineReviews = useMemo(
    () =>
      map(familySelections, (selection) => ({
        ...selection,
        actions: getConfiguredPipelineActions(selection.stainFamily, study),
      })),
    [familySelections, study]
  );
  const invalidPipelineReviews = useMemo(
    () => filter(pipelineReviews, ({ canRunPostIngestPipeline }) => !canRunPostIngestPipeline),
    [pipelineReviews]
  );
  const finishDisabledMessage =
    size(invalidPipelineReviews) >= 1
      ? size(invalidPipelineReviews) === 1
        ? 'Resolve the blocked initial image analysis prerequisite to continue.'
        : `${size(
            invalidPipelineReviews
          )} selected stain-family pipelines are blocked. Adjust the slide selection to continue.`
      : undefined;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const activeStateHandlers = useState(0);
  const [activeStep, setActiveStep] = activeStateHandlers;
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const mifSelection = find(pipelineReviews, ({ stainFamily }) => stainFamily === 'mIF');
  const availableDnaChannels = mifSelection?.availableDnaChannels ?? [];
  const defaultDnaChannel = last(availableDnaChannels);
  const hasMultipleDnaChannels = size(availableDnaChannels) > 1;
  const dnaChannelOptions = useMemo(
    () =>
      map(availableDnaChannels, (dnaChannel) => ({
        value: dnaChannel,
        text: stainTypeIdToDisplayName(dnaChannel),
      })),
    [availableDnaChannels, stainTypeIdToDisplayName]
  );
  const formMethods = useForm<ManualPostIngestFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema[activeStep]),
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = formMethods;
  const selectedDnaChannel = last(watch('dnaChannels') || []);
  const effectiveDnaChannel = hasMultipleDnaChannels ? selectedDnaChannel : defaultDnaChannel;
  useTrackActionFormDirty(formMethods.formState.isDirty);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({ 0: false, 1: false });

  const checkValidationAndSetIsStepFailed = async (stepIndex: number) => {
    const isValid = await trigger();
    setIsStepFailed((previousState) => ({ ...previousState, [stepIndex]: !isValid }));
    return isValid;
  };

  const runManualPostIngestPipelineMutation = useMutation(
    (requests: ReturnType<typeof buildFamilyRequests>) =>
      Promise.all(map(requests, (request) => runManualPostIngestPipeline(request))),
    {
      onError: () => {
        enqueueSnackbar('Could not start initial image analysis', { variant: 'error' });
      },
      onSuccess: () => {
        enqueueSnackbar('Initial image analysis started', { variant: 'success' });
      },
      onSettled() {
        closeSnackbar(SNACK_BAR_KEY_RUN_MANUAL_POST_INGEST_PIPELINE);
      },
    }
  );

  const onSubmit = async (values: ManualPostIngestFormValues) => {
    if (!studyId) {
      return;
    }

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for initial image analysis to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MANUAL_POST_INGEST_PIPELINE,
      autoHideDuration: null,
    });

    await runManualPostIngestPipelineMutation.mutateAsync(
      buildFamilyRequests({ familySelections, studyId, values, dnaChannel: effectiveDnaChannel })
    );
    onClose(undefined, true);
  };

  const totalSlideCount = sumBy(familySelections, ({ slides }) => size(slides));
  const steps = [
    {
      label: 'Review pipeline actions',
      subLabel:
        size(familySelections) > 1
          ? `${size(familySelections)} pipelines for ${totalSlideCount} slides`
          : `${totalSlideCount} slides`,
      content: (
        <Grid container direction="column" spacing={2} mt={1} maxHeight="60vh" sx={{ overflowY: 'auto' }}>
          <Grid item>
            <Typography variant="body1">
              {size(familySelections) > 1
                ? 'The selection contains brightfield and mIF slides. Each stain family runs as a separate pipeline.'
                : `Initial image analysis will run for the selected ${
                    stainFamilyLabels[familySelections[0]?.stainFamily]
                  } slides.`}
            </Typography>
          </Grid>
          {map(
            pipelineReviews,
            ({
              stainFamily,
              slides,
              actions,
              canRunPostIngestPipeline,
              postIngestPipelineReason,
              availableDnaChannels: familyDnaChannels,
            }) => (
              <Grid item key={stainFamily}>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2">
                    {stainFamilyLabels[stainFamily]} pipeline · {size(slides)} {size(slides) === 1 ? 'slide' : 'slides'}
                  </Typography>
                  {!canRunPostIngestPipeline && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      {postIngestPipelineReason}
                    </Alert>
                  )}
                  {stainFamily === 'mIF' && canRunPostIngestPipeline && size(familyDnaChannels) > 1 ? (
                    <Box sx={{ mt: 2 }}>
                      <ChannelsController
                        name="dnaChannels"
                        error={false}
                        methods={formMethods}
                        casesParams={casesParams}
                        channelOptions={dnaChannelOptions}
                        allowedChannelNames={familyDnaChannels}
                        defaultSelectedChannelsOnLoad={defaultDnaChannel ? [defaultDnaChannel] : undefined}
                        label="DNA channel"
                        placeholder="Select a DNA channel"
                        helperText="The last shared DNA channel is selected by default."
                        singleSelection
                      />
                    </Box>
                  ) : stainFamily === 'mIF' && canRunPostIngestPipeline && effectiveDnaChannel ? (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      DNA channel: {stainTypeIdToDisplayName(effectiveDnaChannel)}
                    </Typography>
                  ) : null}
                  <Box component="ol" sx={{ mb: 0, mt: 1, pl: 3 }}>
                    {map(actions, (action) => (
                      <Box component="li" key={action} sx={{ mb: 0.5 }}>
                        <Typography variant="body2">{action}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            )
          )}
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Models and additional actions are resolved from the study&apos;s initial image analysis settings.
            </Typography>
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(0),
    },
    {
      label: 'Job name and description',
      subLabel: activeStep !== 1 && watch('jobName'),
      content: (
        <JobNameAndDescriptionStep
          control={control}
          errors={errors}
          register={register}
          onChangeName={() => checkValidationAndSetIsStepFailed(1)}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(1),
    },
  ];

  if (isLoadingSelection) {
    return (
      <Alert severity="info">
        <AlertTitle>Checking slide selection</AlertTitle>
        Resolving the selected slides and their stain families.
      </Alert>
    );
  }

  if (selectionError || !studyId || !hasSelectedSlides || !size(familySelections)) {
    return (
      <Alert severity="error">
        <AlertTitle>Unable to run initial image analysis</AlertTitle>
        {selectionError || (!studyId ? 'Select a study first.' : 'Select at least one slide.')}
      </Alert>
    );
  }

  return (
    <ActionStepper
      activeStateHandlers={activeStateHandlers}
      handleSubmit={handleSubmit(onSubmit)}
      steps={steps}
      setActiveStepForValidation={setActiveStep}
      isStepFailed={isStepFailed}
      validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
      finishDisabled={Boolean(size(invalidPipelineReviews)) || Boolean(mifSelection && !effectiveDnaChannel)}
      finishDisabledMessage={finishDisabledMessage}
    />
  );
};
